import type { GameContent } from "@bastion/data";
import { GameEngine } from "./engine/GameEngine";
import type { GameState } from "./types";
import { isReplayFile, type ReplayEntry, type ReplayFile, type ReplayOutcome } from "./replay";

export interface ReplayPlaybackResult {
  engine: GameEngine;
  playbackSeconds: number;
  entryCursor: number;
  complete: boolean;
  outcomeMatches: boolean | null;
}

export class ReplayPlayer {
  private engine: GameEngine;
  private entryCursor = 0;
  private playbackSeconds = 0;
  private complete = false;
  private outcomeMatches: boolean | null = null;

  constructor(
    private readonly content: GameContent,
    private readonly replay: ReplayFile,
  ) {
    if (!isReplayFile(replay)) throw new Error("战斗回放格式无效或版本不受支持");
    this.engine = this.createEngine();
  }

  get state(): GameState {
    return this.engine.state;
  }

  get durationSeconds(): number {
    return this.replay.durationSeconds;
  }

  get entries(): readonly ReplayEntry[] {
    return this.replay.entries;
  }

  get currentTime(): number {
    return this.playbackSeconds;
  }

  get cursor(): number {
    return this.entryCursor;
  }

  get isComplete(): boolean {
    return this.complete;
  }

  get resultMatches(): boolean | null {
    return this.outcomeMatches;
  }

  getEngine(): GameEngine {
    return this.engine;
  }

  setEngine(engine: GameEngine): void {
    this.engine = engine;
  }

  getContentFingerprint(): string {
    return this.replay.contentFingerprint;
  }

  getEntryTime(order: number): number {
    let elapsed = 0;
    for (const entry of this.replay.entries) {
      if (entry.order === order) return elapsed;
      if (entry.kind === "advance") elapsed += entry.deltaSeconds;
    }
    return this.replay.durationSeconds;
  }

  getSeekStep(): number {
    const durations = this.replay.entries
      .filter(
        (entry): entry is Extract<ReplayEntry, { kind: "advance" }> => entry.kind === "advance",
      )
      .map((entry) => entry.deltaSeconds)
      .filter((duration) => duration > 0);
    return durations.length ? Math.min(...durations) : 0.01;
  }

  advanceBy(seconds: number): ReplayPlaybackResult {
    if (!Number.isFinite(seconds) || seconds < 0) throw new Error("回放推进时间无效");
    let remaining = seconds;
    this.engine.setReplayEffectsMuted(false);
    while (this.entryCursor < this.replay.entries.length) {
      const entry = this.replay.entries[this.entryCursor];
      if (entry.kind === "advance") {
        if (entry.deltaSeconds > remaining + 1e-9) break;
        this.applyEntry(entry);
        this.playbackSeconds += entry.deltaSeconds;
        remaining -= entry.deltaSeconds;
      } else {
        this.applyEntry(entry);
      }
      this.entryCursor += 1;
    }
    this.complete = this.entryCursor >= this.replay.entries.length;
    this.outcomeMatches = this.complete ? this.checkOutcome() : null;
    return this.snapshot();
  }

  setMuted(muted: boolean): void {
    this.engine.setReplayEffectsMuted(muted);
  }

  seek(targetSeconds: number): ReplayPlaybackResult {
    if (!Number.isFinite(targetSeconds) || targetSeconds < 0) {
      throw new Error("回放时间必须是非负有限数值");
    }
    const target = Math.min(targetSeconds, this.replay.durationSeconds);
    this.rebuildEngine();
    this.engine.setReplayEffectsMuted(true);
    try {
      while (this.entryCursor < this.replay.entries.length) {
        const entry = this.replay.entries[this.entryCursor];
        if (entry.kind === "advance") {
          if (this.playbackSeconds + entry.deltaSeconds > target + 1e-9) break;
          this.engine.advanceTo(entry.targetTime);
          this.playbackSeconds += entry.deltaSeconds;
          this.entryCursor += 1;
          continue;
        }
        if (this.playbackSeconds > target + 1e-9 && target < this.replay.durationSeconds - 1e-9)
          break;
        this.applyEntry(entry);
        this.entryCursor += 1;
      }
    } finally {
      this.engine.setReplayEffectsMuted(false);
    }
    this.complete = this.entryCursor >= this.replay.entries.length;
    this.outcomeMatches = this.complete ? this.checkOutcome() : null;
    return this.snapshot();
  }

  snapshot(): ReplayPlaybackResult {
    return {
      engine: this.engine,
      playbackSeconds: this.playbackSeconds,
      entryCursor: this.entryCursor,
      complete: this.complete,
      outcomeMatches: this.outcomeMatches,
    };
  }

  private createEngine(): GameEngine {
    const engine = new GameEngine(this.content, {
      seed: this.replay.seed,
      difficulty: this.replay.difficulty,
      mode: this.replay.mode,
      stepSeconds: this.replay.stepSeconds,
    });
    engine.setReplayPlayback(true);
    engine.start(this.replay.mapId);
    engine.setReplayRecording(false);
    if (engine.getReplayContentFingerprint() !== this.replay.contentFingerprint) {
      throw new Error("战斗回放内容与当前游戏版本不匹配");
    }
    return engine;
  }

  private rebuildEngine(): void {
    this.engine = this.createEngine();
    this.entryCursor = 0;
    this.playbackSeconds = 0;
    this.complete = false;
    this.outcomeMatches = null;
    this.engine.setReplayEffectsMuted(false);
  }

  private applyEntry(entry: ReplayEntry): void {
    switch (entry.kind) {
      case "start":
        if (this.playbackSeconds !== 0 || this.engine.state.wave !== 0) {
          throw new Error(`回放开局标记位置无效（序号 ${entry.order}）`);
        }
        return;
      case "restart":
        this.engine.resetForReplayRestart();
        return;
      case "advance":
        this.engine.advanceTo(entry.targetTime);
        return;
      case "command": {
        let accepted = true;
        try {
          this.engine.dispatch(entry.command);
        } catch {
          accepted = false;
        }
        if (accepted !== entry.accepted) {
          throw new Error(`回放命令结果不一致（序号 ${entry.order}）`);
        }
      }
    }
  }

  private checkOutcome(): boolean | null {
    const expected: ReplayOutcome | undefined = this.replay.outcome;
    if (!expected) return null;
    const engineTime = this.engine.time;
    return (
      this.engine.state.status === expected.status &&
      this.engine.state.wave === expected.wave &&
      Math.abs(engineTime - expected.completedAt) <= 1e-6
    );
  }
}
